// src/components/CalendarEventModal.js
//
// Create/edit/remove modal for the standalone Research Calendar view page
// (ResearchCalendarPage.js). This is a sibling of ScheduleVisitModal.js, NOT a
// replacement — ScheduleVisitModal stays wired to PatientDetailPage exactly as
// it was. This modal is for booking events directly from the calendar grid,
// where there's no existing patient record driving the fields, so it collects
// the patient name itself and talks to the same Apps Script web app
// (upsertCalendarEvent / deleteCalendarEvent) via researchCalendarService.js.

import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CalendarPlus,
  X,
  Trash2,
  ExternalLink,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { auth } from "../firebase";
import { fetchActiveCoordinators } from "../services/coordinatorService";
import SearchableSelect from "./SearchableSelect";
import {
  upsertCalendarEvent,
  deleteCalendarEvent,
} from "../services/researchCalendarService";
import { useCalendarRooms } from "./researchCalendarRooms";
import {
  useRoomBookingCheck,
  RoomCheckDialog,
  slotChanged,
  fmtRange,
} from "./roomBookingCheck";

const inputCls =
  "w-full px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition";
const inputDateCls = `${inputCls} [color-scheme:dark]`;
const labelCls =
  "block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1";
const selectCls =
  "w-full appearance-none px-3 pr-9 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 hover:bg-white/[0.06] focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-500/20 cursor-pointer transition";

export default function CalendarEventModal({
  open,
  onClose,
  event = {}, // { eventId, patientName, patientToken, date, startTime, endTime, room, otherRoom, coordinator, coordinatorEmail, study, visit, notes, createdBy, eventLink }
  // Events already on the calendar, used only to warn about a room that's
  // already booked at the same time. Optional: callers that don't pass it just
  // don't get the warning, so nothing existing breaks.
  existingEvents = [],
  onSaved,
  onRemoved,
}) {
  // This site's rooms — data, not a hardcoded list. Defaults render immediately
  // and are replaced if the site has its own configured.
  const { rooms } = useCalendarRooms();
  const [coordinators, setCoordinators] = useState([]);
  const [patientName, setPatientName] = useState("");
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [room, setRoom] = useState("");
  const [otherRoom, setOtherRoom] = useState("");
  const [coordinator, setCoordinator] = useState("");
  const [study, setStudy] = useState("");
  const [visit, setVisit] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState("");

  // The shared "is this room actually free?" confirmation (roomBookingCheck.js),
  // the same one PatientDetailPage's ScheduleVisitModal uses. It re-reads the
  // chosen day from the calendar itself; `existingEvents` is only a fallback for
  // when that read fails.
  const {
    conflicts: roomConflicts,
    requestCheck,
    dialogProps,
  } = useRoomBookingCheck({
    open,
    resetKey: event.eventId,
    date,
    startTime,
    endTime,
    room,
    otherRoom,
    excludeEventId: event.eventId,
    seedEvents: existingEvents,
  });

  useEffect(() => {
    if (!open) return;
    setPatientName(event.patientName || "");
    setDate(event.date || "");
    setStartTime(event.startTime || "");
    setEndTime(event.endTime || "");
    setRoom(event.room || "");
    setOtherRoom(event.otherRoom || "");
    setCoordinator(event.coordinator || "");
    setStudy(event.study || "");
    setVisit(event.visit || "");
    setNotes(event.notes || "");
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, event.eventId]);

  // Default to the site's first room once the room list is known. Kept separate
  // from the hydration effect above because rooms load asynchronously — this
  // only fills a room in when the user hasn't got one, so it never clobbers an
  // existing event's room or a choice the user just made.
  useEffect(() => {
    if (open && !room && rooms.length) setRoom(rooms[0].name);
  }, [open, room, rooms]);

  useEffect(() => {
    let alive = true;
    if (open) {
      fetchActiveCoordinators()
        .then((list) => alive && setCoordinators(list))
        .catch(() => alive && setCoordinators([]));
    }
    return () => {
      alive = false;
    };
  }, [open]);

  if (!open) return null;

  const coordinatorEmail =
    coordinators.find((c) => c.name === coordinator)?.email || "";

  const roomBeingBooked = room === "Other" ? otherRoom.trim() || "this room" : room;

  // Only the room and the slot decide whether a booking can clash, so editing
  // just the notes or the study on an existing event doesn't re-ask.
  const needsRoomCheck =
    !event.eventId ||
    slotChanged({ date, startTime, endTime, room, otherRoom }, event);

  const validate = () => {
    if (!patientName.trim()) return "Enter a patient name.";
    if (!date) return "Pick a date.";
    if (!startTime || !endTime) return "Pick a start and end time.";
    if (endTime <= startTime) return "End time must be after start time.";
    if (!room) return "Pick a room.";
    // Naming the room under "Other" is optional — plenty of visits genuinely
    // are just "somewhere else". Without a name RegX can't match it against
    // anything, which the room-check popup says outright rather than blocking.
    return "";
  };

  // Save is a two-step: the room-check popup first, then the actual write.
  // Plenty of visits are booked straight from Google Calendar and arrive with
  // no room on them, so RegX can never promise a room is free — the popup is
  // there to make someone look before they book.
  const handleSave = () => {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setError("");
    if (needsRoomCheck) {
      requestCheck();
      return;
    }
    performSave();
  };

  const performSave = async () => {
    dialogProps.onCancel();
    setError("");
    setSaving(true);
    // "Created by" is immutable — stamp it once on the initial create, and on
    // every later edit just resend whatever it already was so it doesn't get
    // silently overwritten by whoever happens to make the next change.
    const createdBy =
      event.createdBy || auth.currentUser?.email || "unknown";
    try {
      const body = await upsertCalendarEvent({
        eventId: event.eventId || "",
        date,
        startTime,
        endTime,
        room,
        otherRoom,
        coordinatorName: coordinator,
        coordinatorEmail,
        study,
        visit,
        notes: notes.trim(),
        patientName: patientName.trim(),
        patientToken: event.patientToken || "",
        createdBy,
      });
      onSaved?.({
        patientName: patientName.trim(),
        patientToken: event.patientToken || "",
        date,
        startTime,
        endTime,
        room,
        otherRoom,
        coordinator,
        coordinatorEmail,
        study,
        visit,
        notes: notes.trim(),
        createdBy,
        eventId: body.eventId,
        eventLink: body.htmlLink || "",
      });
      onClose?.();
    } catch (e) {
      setError(e.message || "Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!event.eventId) {
      onClose?.();
      return;
    }
    setRemoving(true);
    setError("");
    try {
      await deleteCalendarEvent(event.eventId);
      onRemoved?.(event.eventId);
      onClose?.();
    } catch (e) {
      setError(e.message || "Could not remove the event.");
    } finally {
      setRemoving(false);
    }
  };

  const busy = saving || removing;

  return (
    <>
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={() => !busy && !dialogProps.open && onClose?.()}
      >
        <motion.div
          className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#0e1016]/95 shadow-2xl overflow-hidden"
          initial={{ opacity: 0, scale: 0.96, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 12 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-gradient-to-r from-purple-500/10 to-blue-500/10">
            <div className="flex items-center gap-2">
              <CalendarPlus className="w-4 h-4 text-purple-300" />
              <div>
                <h3 className="text-sm font-semibold text-white">
                  {event.eventId ? "Edit Visit" : "New Visit"}
                </h3>
                <p className="text-[11px] text-slate-400">Research calendar</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => !busy && onClose?.()}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <div className="px-5 py-4 space-y-3 max-h-[70vh] overflow-y-auto">
            <label className="block">
              <span className={labelCls}>Patient Name</span>
              <input
                className={inputCls}
                value={patientName}
                placeholder="Full name"
                onChange={(e) => setPatientName(e.target.value)}
              />
            </label>

            <div className="grid grid-cols-3 gap-3">
              <label className="block">
                <span className={labelCls}>Date</span>
                <input
                  type="date"
                  className={inputDateCls}
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </label>
              <label className="block">
                <span className={labelCls}>Start</span>
                <input
                  type="time"
                  className={inputDateCls}
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                />
              </label>
              <label className="block">
                <span className={labelCls}>End</span>
                <input
                  type="time"
                  className={inputDateCls}
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className={labelCls}>Room</span>
                <div className="relative">
                  <select
                    className={selectCls}
                    value={room}
                    onChange={(e) => setRoom(e.target.value)}
                  >
                    {rooms.map((r) => (
                      <option key={r.name} value={r.name} className="bg-[#0e1016]">
                        {r.name}
                      </option>
                    ))}
                  </select>
                </div>
              </label>
              <label className="block">
                <span className={labelCls}>Coordinator</span>
                <div className="relative">
                  <SearchableSelect
                    value={coordinator}
                    options={coordinators.map((c) => c.name)}
                    onChange={setCoordinator}
                    clearOption={{ value: "", label: "— none —" }}
                    optionHint={(n) => (coordinators.find((c) => c.name === n)?.email ? "" : " (no email)")}
                    placeholder="Type to find a coordinator"
                    noMatchText="No active coordinator matches that."
                    inputClassName={`${selectCls} pl-9`}
                  />
                </div>
              </label>
            </div>

            {room === "Other" && (
              <label className="block">
                <span className={labelCls}>Which room? (optional)</span>
                <input
                  className={inputCls}
                  value={otherRoom}
                  placeholder="e.g. Lab, Imaging — leave blank for just “Other”"
                  onChange={(e) => setOtherRoom(e.target.value)}
                />
                <span className="mt-1 block text-[10px] text-slate-500">
                  Naming it lets RegX warn you if it's already booked.
                </span>
              </label>
            )}

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className={labelCls}>Study</span>
                <input
                  className={inputCls}
                  value={study}
                  placeholder="Study name"
                  onChange={(e) => setStudy(e.target.value)}
                />
              </label>
              <label className="block">
                <span className={labelCls}>Visit</span>
                <input
                  className={inputCls}
                  value={visit}
                  placeholder="e.g. Screening, V2"
                  onChange={(e) => setVisit(e.target.value)}
                />
              </label>
            </div>

            <label className="block">
              <span className={labelCls}>Notes</span>
              <textarea
                className={`${inputCls} resize-none`}
                rows={3}
                value={notes}
                placeholder="Anything the coordinator should know before this visit…"
                onChange={(e) => setNotes(e.target.value)}
              />
            </label>

            {roomConflicts.length > 0 && (
              <div className="rounded-lg border border-amber-400/30 bg-amber-500/10 px-3 py-2.5 text-[11px] text-amber-200">
                <div className="flex items-center gap-1.5 font-semibold text-amber-100">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                  {roomBeingBooked} looks double-booked
                </div>
                <ul className="mt-1.5 space-y-0.5">
                  {roomConflicts.slice(0, 3).map((ev) => (
                    <li key={ev.id}>
                      • {fmtRange(ev)} — {ev.patientName || "Visit"}
                      {ev.coordinatorName ? ` (${ev.coordinatorName})` : ""}
                    </li>
                  ))}
                  {roomConflicts.length > 3 && (
                    <li>• and {roomConflicts.length - 3} more…</li>
                  )}
                </ul>
                <p className="mt-1.5 text-amber-200/80">
                  Please double-check the room and time before saving. You can
                  still book it if that's intentional.
                </p>
              </div>
            )}

            {coordinator && !coordinatorEmail && (
              <p className="text-[11px] text-amber-300/90">
                {coordinator} has no email on file — they won't get a calendar
                invite. The visit still lands on the Research calendar.
              </p>
            )}

            {error && (
              <p className="text-[12px] text-rose-300 bg-rose-500/10 border border-rose-400/20 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            {event.eventLink && (
              <a
                href={event.eventLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[12px] text-purple-300 hover:text-purple-200"
              >
                View on Google Calendar
                <ExternalLink className="w-3 h-3" />
              </a>
            )}

            {event.createdBy && (
              <p className="text-[10px] text-slate-500">
                Created by {event.createdBy.split("@")[0]}
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-white/10">
            {event.eventId ? (
              <button
                type="button"
                onClick={handleRemove}
                disabled={busy}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium text-rose-300 hover:text-white hover:bg-rose-500/20 border border-rose-400/20 transition disabled:opacity-50"
              >
                {removing ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Trash2 className="w-3.5 h-3.5" />
                )}
                Remove
              </button>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => !busy && onClose?.()}
                disabled={busy}
                className="px-3 py-2 rounded-xl text-xs font-medium text-slate-300 hover:text-white hover:bg-white/10 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={busy}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-white bg-gradient-to-r from-purple-500 to-blue-500 shadow-[0_4px_12px_-2px_rgba(139,92,246,0.5)] hover:brightness-110 transition disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <CalendarPlus className="w-3.5 h-3.5" />
                )}
                {event.eventId ? "Save changes" : "Add to calendar"}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
    {/* Room double-booking check — the shared confirmation, same as
        PatientDetailPage's ScheduleVisitModal. */}
    <RoomCheckDialog {...dialogProps} onConfirm={performSave} saving={saving} />
    </>
  );
}
